import { clamp } from "lodash-es";
import { v4 as uuid } from "uuid";
import type { Directive, DirectiveBinding } from "vue";
import { nextTick } from "vue";

import { isElementInViewport, terminateEvent } from "@/utils/dom/dom";

import { autofocusStatus } from "./autofocus";

/**
 * Enable keyboard navigation through a list of '.list-item's
 * using the up- / down-arrow keys
 *
 * Modifiers (may be passed as values as well, allowing them to be set conditionally):
 * select: Automatically selects the next/previous item on key press
 * no-wrap: Keyboard navigation will not wrap around the end of the list
 * soft-focus: Only focus on the current item if the focus is already on the list
 *             (if true, soft-focus-initial is effectively true as well)
 * soft-focus-initial: When the list initializes, don't pull focus to the first item
 * ignore-trigger: Key presses on the trigger element will be ignored
 * horizontal: Navigate horizontally (left/right arrow keys). Default is vertical (up/down)
 * buttons: The list items are buttons (use 'activated' class instead of 'current')
 * no-scroll-on-hover: Prevents scrolling when hovering over list items (scroll still works for keyboard nav)
 * roving-tabindex: Only the current item has tabindex="0", all others have tabindex="-1".
 *                  Tab/Shift+Tab exits the list instead of visiting each item.
 *
 * Value:
 * Value.selector: The css selector to identify the list items (default: '.list-item')
 *
 */
export function keyboardNavigation(): Directive<
  HTMLElement,
  {
    select?: boolean;
    "soft-focus-initial"?: boolean;
    "ignore-trigger"?: boolean;
    "no-scroll-on-hover"?: boolean;
    "roving-tabindex"?: boolean;
    selector?: string;
  }
> {
  const navId = uuid();
  let listenerController = new AbortController();
  let currentIndex = 0;
  let activeElement: HTMLElement | null = null;
  let listItems = new Array<HTMLElement>();
  let autoSelect = false;
  let wrapDisabled = false;
  let ignoreTrigger = false;
  let pullFocus = false;
  let pullFocusInitial = false;
  let horizontal = false;
  let noScrollOnHover = false;
  let rovingTabindex = false;
  let itemSelector = "";
  let current = "current";

  return {
    mounted(el, binding) {
      readAttributes(binding);

      // Small delay to ensure any previous keyboard-navigation is unmounted first
      // (eg. when using TabbedModal with keyboard-navigation in multiple tabs)
      void nextTick().then(async () => {
        el.setAttribute("data-nav-id", navId);

        initListItems(el);
        initCurrent(el);
        applyRovingTabindex();
        activeElement = await initActiveElement();

        el.addEventListener("keydown", keyHandler);
        void setCurrent(currentIndex, !pullFocusInitial);
      });
    },

    updated(el, binding) {
      const pullFocusWasDeferred = !pullFocusInitial;
      readAttributes(binding);

      // helps not lose focus after DOM updates
      const oldItem = currentItem(); // remember previously focused item
      initListItems(el);
      if (oldItem && listItems.includes(oldItem)) {
        currentIndex = listItems.indexOf(oldItem); // keep focus on the same item
      } else {
        initCurrent(el); // fallback
      }
      applyRovingTabindex();
      // like the autofocus directive: turning soft-focus-initial off later pulls the focus now
      if (pullFocusWasDeferred && pullFocusInitial) {
        void setCurrent(currentIndex);
      }
    },

    unmounted(el) {
      listenerController.abort();

      if (!ignoreTrigger) {
        activeElement?.removeEventListener("keydown", keyHandler);
      }

      el.removeEventListener("keydown", keyHandler);
      currentIndex = -1;

      el.removeAttribute("data-nav-id");
    },
  };

  function initListItems(el: HTMLElement) {
    listenerController.abort();
    listenerController = new AbortController();
    listItems = Array.from(el.querySelectorAll(`${itemSelector}:not(.static):not(.disabled)`));
    if (autoSelect) {
      initClickListeners();
    } else {
      initPointerListeners();
    }

    /**
     * Ensure the currentIndex gets updated when the user clicks on a list item
     */
    function initClickListeners() {
      listItems.forEach((item, index) => {
        item.addEventListener("click", () => void setCurrent(index), {
          signal: listenerController.signal,
          capture: true,
        });
      });
    }

    /**
     * Ensure the currentIndex is updated when the user hovers over a list item
     */
    function initPointerListeners() {
      listItems.forEach((item, index) => {
        item.classList.add("no-hover");
        item.addEventListener("pointerenter", () => void setCurrent(index, true, noScrollOnHover), {
          signal: listenerController.signal,
        });
        item.addEventListener("focus", () => setCurrentIfDifferent(index), {
          signal: listenerController.signal,
        });
      });
    }
  }

  /**
   * When the list initializes, either focus on the active (selected) item,
   * or focus on the first item if none are active
   */
  function initCurrent(el: HTMLElement) {
    const activeClasses = ["selected", "active", "activated", current]; // our commonly-used classes for active elements
    const excludedClasses = ["static", "disabled"];

    const notSelector = excludedClasses.map((c) => `:not(.${c})`).join("");
    const selector = activeClasses.map((c) => `${itemSelector}.${c}${notSelector}`).join(", ");

    const active = el.querySelector<HTMLElement>(selector);
    if (active) {
      const index = listItems.indexOf(active);
      currentIndex = index >= 0 ? index : 0;
    } else {
      currentIndex = 0;
    }
  }

  async function initActiveElement() {
    if (ignoreTrigger) {
      return null;
    }
    // Wait for any autofocus directive to finish
    let retries = 10;
    while (autofocusStatus.pending && retries > 0) {
      await nextTick();
      retries--;
    }
    // Add key listener to both the trigger and the list
    const active = document.activeElement as HTMLElement | null;
    active?.addEventListener("keydown", keyHandler);
    return active;
  }

  /**
   * Set the current item if the given index is different from the current one
   * (avoids infinite loop when setting focus on the current item)
   */
  function setCurrentIfDifferent(index: number) {
    if (index !== currentIndex || !currentItem()?.classList.contains(current)) {
      void setCurrent(index);
      simulatePointerEvent();
    }
  }

  function applyRovingTabindex() {
    if (!rovingTabindex) return;
    listItems.forEach((item, index) => {
      item.tabIndex = index === currentIndex ? 0 : -1;
    });
  }

  async function setCurrent(index: number, skipFocus?: boolean, skipScroll?: boolean) {
    if (rovingTabindex) currentItem()?.setAttribute("tabindex", "-1");
    currentItem()?.classList.remove(current);
    currentIndex = index;
    currentItem()?.classList.add(current);
    if (rovingTabindex) currentItem()?.setAttribute("tabindex", "0");
    // the currentItem might not yet be correctly positioned (context menu)
    // wait for it not to scroll the board with the focus
    await nextTick();
    await nextTick();
    // Only move focus to the current item if the focus is already somewhere on the list
    // (or if in 'pull focus' mode)
    if (pullFocus || hasListFocus()) {
      if (!skipFocus) {
        currentItem()?.focus();
      }

      // Scroll into view only if the element is not visible in the viewport
      // Skip scrolling when hovering (skipScroll=true)
      if (!skipScroll) {
        const current = currentItem();
        if (current && !isElementInViewport(current)) {
          current.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
      }
    }
  }

  function hasListFocus() {
    return document.activeElement?.closest(`[data-nav-id="${navId}"]`);
  }

  function simulatePointerEvent() {
    const newItem = currentItem();
    if (newItem) {
      newItem.dispatchEvent(new PointerEvent("pointerenter"));
      if (autoSelect) {
        newItem.click();
      }
      newItem.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }

  function currentItem() {
    return listItems[currentIndex];
  }

  /**
   * Return the wrapped or non-wrapped index, depending on the
   * given wrapDisabled modifier
   */
  function adjustIndex(index: number) {
    return wrapDisabled ? noWrap(index) : wrap(index);
  }

  /**
   * Return the given index if it's within 0 <= index <= listItems.length-1,
   * or give the nearest valid index (i.e. -1 --> 0, length --> length-1)
   */
  function noWrap(index: number) {
    return clamp(index, 0, listItems.length ? listItems.length - 1 : 0);
  }

  /**
   * Return the given index if it's within the array of listItems,
   * or give the index that simulates the list wrapping (i.e. -1 --> last item)
   */
  function wrap(index: number) {
    const len = listItems.length;
    return len === undefined ? -1 : (index + len) % len;
  }

  function keyHandler(event: KeyboardEvent) {
    // With soft-focus, only handle events when focus is inside the list
    if (!pullFocus && !hasListFocus()) {
      return;
    }

    switch (event.key) {
      case !horizontal && "ArrowUp":
      case horizontal && "ArrowLeft":
        terminateEvent(event);
        void setCurrent(adjustIndex(currentIndex - 1));
        simulatePointerEvent();
        break;
      case !horizontal && "ArrowDown":
      case horizontal && "ArrowRight":
        terminateEvent(event);
        void setCurrent(adjustIndex(currentIndex + 1));
        simulatePointerEvent();
        break;
      case "Enter":
        terminateEvent(event);
        currentItem()?.click();
        break;
      case " ":
        if (!(document.activeElement instanceof HTMLInputElement)) {
          terminateEvent(event);
          currentItem()?.click();
        }
        break;
    }
  }

  /**
   * Check if the given attribute is set in the binding
   * (either as a modifier, arg or value)
   */
  function hasAttr(binding: DirectiveBinding, name: string): boolean {
    return !!(binding.modifiers[name] || binding.arg === name || binding.value?.[name]);
  }

  function stringAttrValue(binding: DirectiveBinding, name: string): string | undefined {
    const val = binding.value?.[name];
    return typeof val === "string" ? val : undefined;
  }

  /**
   * Read the attributes from the binding and set the local state accordingly
   */
  function readAttributes(binding: DirectiveBinding) {
    autoSelect = hasAttr(binding, "select");
    wrapDisabled = hasAttr(binding, "no-wrap");
    ignoreTrigger = hasAttr(binding, "ignore-trigger");
    pullFocus = !hasAttr(binding, "soft-focus");
    pullFocusInitial = !hasAttr(binding, "soft-focus-initial");
    horizontal = hasAttr(binding, "horizontal");
    noScrollOnHover = hasAttr(binding, "no-scroll-on-hover");
    rovingTabindex = hasAttr(binding, "roving-tabindex");
    current = hasAttr(binding, "buttons") ? "activated" : "current";
    itemSelector = stringAttrValue(binding, "selector") || ".list-item";
  }
}
